// ============================================
// PAGE - Login
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLogin, useGetSucursalesByRut, useGetUsuariosLogin, type AlmacenSucursal, type UsuarioLogin } from '@almacenes/core';
import { Button, Input } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import pcstoreImg from '@/assets/images/pcstore.svg';
import { useRutFormatter } from '@/hooks/useRutFormatter';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { mutate: login, isPending, error } = useLogin();
  const { rawValue: rut, formattedValue: formattedRut, handleChange: handleRutChange, isValid } = useRutFormatter();
  const rutInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'rut' | 'sucursal' | 'usuario' | 'credentials'>('rut');
  const [password, setPassword] = useState('');
  const [rutError, setRutError] = useState('');
  const [searchRut, setSearchRut] = useState<string | null>(null);
  const [selectedSucursal, setSelectedSucursal] = useState<AlmacenSucursal | null>(null);
  const [selectedUsuario, setSelectedUsuario] = useState<UsuarioLogin | null>(null);
  
  const { data: sucursales, isLoading: isLoadingSucursales } = useGetSucursalesByRut(searchRut);
  const { data: usuarios, isLoading: isLoadingUsuarios } = useGetUsuariosLogin(selectedSucursal?.id ?? null);

  const handleRutSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar RUT antes de buscar
    if (!isValid()) {
      setRutError('El RUT ingresado no es válido');
      return;
    }
    
    setRutError('');
    setSearchRut(rut);
  };

  // Focus automático en el input del RUT
  useEffect(() => {
    if (step === 'rut' && rutInputRef.current) {
      rutInputRef.current.focus();
    }
  }, [step]);

  // Validar RUT mientras el usuario escribe
  useEffect(() => {
    // Solo validar si el usuario ha escrito suficientes caracteres
    if (formattedRut.length >= 11) { // RUT formateado completo es al menos 11 caracteres (XX.XXX.XXX-X)
      if (!isValid()) {
        setRutError('El RUT ingresado no es válido');
      } else {
        setRutError('');
      }
    }
  }, [formattedRut, isValid]);

  // Cuando se obtienen las sucursales
  useEffect(() => {
    if (sucursales) {
      if (sucursales.length === 0) {
        setRutError('¡Ups! No pudimos encontrar el RUT ingresado o parece que tu almacén no está activo. Revisa que el número sea correcto o escríbenos para ayudarte a activarlo');
        setSearchRut(null);
      } else if (sucursales.length === 1) {
        // Si solo hay una sucursal, seleccionarla automáticamente
        setSelectedSucursal(sucursales[0]);
        setStep('usuario');
      } else {
        setStep('sucursal');
      }
    }
  }, [sucursales]);

  // Cuando se obtienen los usuarios y solo hay uno
  useEffect(() => {
    if (usuarios && usuarios.length === 1 && !selectedUsuario) {
      setSelectedUsuario(usuarios[0]);
      setStep('credentials');
    }
  }, [usuarios, selectedUsuario]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSucursal || !selectedUsuario) {
      return;
    }
    
    login(
      { 
        almacenId: selectedSucursal.id,
        usuarioId: selectedUsuario.usuarioId,
        password 
      },
      {
        onSuccess: () => {
          navigate('/dashboard');
        },
      }
    );
  };

  return (
    <div className="min-h-screen h-screen flex flex-col overflow-hidden">
      {/* Topbar con logo */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
      </div>

      {/* Layout principal - 2 columnas */}
      <div className="flex-1 grid lg:grid-cols-2 h-full overflow-hidden">
        {/* Columna izquierda - Formulario */}
        <div className="flex items-start justify-center px-8 py-8 h-full overflow-auto" style={{ backgroundColor: '#F3F4F9' }}>
          <div className="w-full max-w-md">
            {/* Título de bienvenida */}
            <div className="mb-12">
              <h1 className="text-title-1">
                !Bienvenido de nuevo!
              </h1>
              <span className="block mt-2 text-sm text-gray-500">
                Gestiona tu almacén de forma simple
              </span>
            </div>

            {/* Card del formulario */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {step === 'rut' ? (
                <>
                  <h2 className="text-title-3 mb-6">Rut del almacén</h2>

                  <form onSubmit={handleRutSubmit} className="space-y-4">
                    <div>
                      <Input
                        ref={rutInputRef}
                        label=""
                        type="text"
                        required
                        value={formattedRut}
                        onChange={(e) => {
                          handleRutChange(e);
                          // Solo limpiar el error si es de validación, no el de "almacén no encontrado"
                          if (rutError && !rutError.includes('¡Ups!')) {
                            setRutError('');
                          }
                        }}
                        placeholder="Ej: 12.345.678-9"
                        className="text-base"
                      />
                      
                      {/* Espacio reservado para mensaje de error */}
                      <div className="h-8 mt-1">
                        {rutError && (
                          <div className={`rounded-lg px-3 py-1.5 text-xs border ${
                            rutError.includes('¡Ups!') 
                              ? 'bg-orange-50 text-orange-800 border-orange-200' 
                              : 'bg-red-50 text-red-800 border-red-200'
                          }`}>
                            {rutError}
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full py-4"
                      size="lg"
                      isLoading={isLoadingSucursales}
                      disabled={!isValid()}
                    >
                      Continuar
                    </Button>

                    <div className="text-center pt-4">
                      <span className="text-body">¿No tienes cuenta? </span>
                      <Link to="/auth/register" className="text-link font-medium">
                        Regístrate aquí
                      </Link>
                    </div>
                  </form>
                </>
              ) : step === 'sucursal' ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-title-3 mb-2">Selecciona tu sucursal</h2>
                    <p className="text-sm text-gray-500">
                      RUT: {formattedRut}
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    {sucursales?.map((sucursal) => (
                      <button
                        key={sucursal.id}
                        type="button"
                        onClick={() => {
                          setSelectedSucursal(sucursal);
                          setStep('usuario');
                        }}
                        className="w-full text-left p-4 border-2 rounded-lg transition-all hover:shadow-md"
                        style={{
                          borderColor: 'rgba(67, 191, 255, 0.5)',
                          backgroundColor: 'rgba(67, 191, 255, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#43BFFF';
                          e.currentTarget.style.backgroundColor = 'rgba(67, 191, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(67, 191, 255, 0.5)';
                          e.currentTarget.style.backgroundColor = 'rgba(67, 191, 255, 0.1)';
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {/* Ícono de tienda */}
                          <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#ffe876' }}>
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{ color: '#11194f' }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                              />
                            </svg>
                          </div>
                          
                          {/* Información de la sucursal: la dirección va primero y en
                              mayor jerarquía visual porque, en negocios con varias
                              sucursales bajo el mismo RUT, el nombre suele repetirse
                              idéntico en todas — la dirección es lo único que en la
                              práctica permite distinguir cuál es cuál de un vistazo. */}
                          <div className="flex-1">
                            <div className="font-semibold text-lg" style={{ color: '#11194f' }}>
                              📍 {sucursal.direccion} {sucursal.numeroDireccion ? `#${sucursal.numeroDireccion}` : ''}
                            </div>
                            <div className="text-sm mt-0.5" style={{ color: '#11194f', opacity: 0.7 }}>
                              {sucursal.nombre}
                            </div>
                          </div>

                          {/* Flecha */}
                          <svg
                            className="w-5 h-5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: '#11194f' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>

                  <Button
                    variant="neutral"
                    className="w-full"
                    onClick={() => {
                      setStep('rut');
                      setSearchRut(null);
                    }}
                  >
                    ← Volver
                  </Button>
                </>
              ) : step === 'usuario' ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-title-3 mb-2">Selecciona el usuario</h2>
                    <p className="text-sm text-gray-500">
                      {selectedSucursal?.nombre}
                    </p>
                  </div>

                  {isLoadingUsuarios ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 mb-6">
                        {usuarios?.map((usuario) => (
                          <button
                            key={usuario.usuarioId}
                            type="button"
                            onClick={() => {
                              setSelectedUsuario(usuario);
                              setStep('credentials');
                            }}
                            className={`w-full text-left p-4 border-2 rounded-lg transition-all hover:shadow-md ${
                              usuario.esPropietario 
                                ? 'border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100' 
                                : 'border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* Ícono de usuario */}
                              <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                                usuario.esPropietario ? 'bg-blue-500' : 'bg-green-500'
                              }`}>
                                <svg
                                  className="w-6 h-6 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              </div>
                              
                              {/* Información del usuario */}
                              <div className="flex-1">
                                <div className="font-semibold text-text-primary text-lg">
                                  {usuario.nombreMostrable}
                                </div>
                                <div className={`text-sm font-medium mt-0.5 ${
                                  usuario.esPropietario ? 'text-blue-700' : 'text-green-700'
                                }`}>
                                  {usuario.esPropietario ? 'Propietario' : 'Vendedor'}
                                </div>
                              </div>

                              {/* Flecha */}
                              <svg
                                className={`w-5 h-5 flex-shrink-0 ${
                                  usuario.esPropietario ? 'text-blue-500' : 'text-green-500'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>

                      <Button
                        variant="neutral"
                        className="w-full"
                        onClick={() => setStep('sucursal')}
                      >
                        ← Volver
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-title-3 mb-1">Iniciar sesión</h2>
                    <p className="text-sm text-gray-500">
                      {selectedSucursal?.nombre}
                    </p>
                    <p className="text-sm font-medium text-text-primary mt-1">
                      {selectedUsuario?.nombreMostrable}
                    </p>
                  </div>

                  {/* Mensaje de error */}
                  {error && (
                    <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
                      {(error as any)?.statusCode === 401 
                        ? 'La contraseña no es correcta. Por favor, inténtalo de nuevo.'
                        : ((error as any)?.message || 'Error al iniciar sesión')}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                      label="Contraseña"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Contraseña"
                      className="text-base"
                    />

                    <div className="text-right">
                      <Link to="/auth/recover-password" className="text-link text-sm" target="_blank" rel="noopener noreferrer">
                        ¿Olvidaste tu contraseña?
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full py-4"
                      size="lg"
                      isLoading={isPending}
                    >
                      Ingresar
                    </Button>

                    <Button
                      variant="neutral"
                      className="w-full"
                      onClick={() => setStep('usuario')}
                    >
                      ← Volver
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha - Ilustración */}
        <div 
          className="hidden lg:flex items-center justify-center p-12 h-full"
          style={{ 
            background: 'linear-gradient(135deg, #FFE27A 0%, #F9D949 100%)'
          }}
        >
          <div className="max-w-2xl">
            <img 
              src={pcstoreImg} 
              alt="E-commerce Illustration" 
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
